import type { Dispatch } from 'redux';

import type { AllAlert, Category } from '~/@types/redux';
import { CATEGORY_IDS } from '~/pages/Home/constants';
import { HomeActions } from '~/pages/Home/redux';

export type BoltItem = {
  label: string;
  slug: string;
  categoryId: number;
  alert?: AllAlert;
};

export type InfoItem = {
  label: string;
  key: string;
  to: string;
};

// Helper to get readable key name from code
export const getKeyName = (code: string): string => {
  const keyMap: Record<string, string> = {
    KeyH: 'H',
    KeyJ: 'J',
    KeyK: 'K',
    KeyL: 'L',
    KeyM: 'M',
    KeyN: 'N',
    KeyO: 'O',
    KeyP: 'P',
    KeyY: 'Y',
    KeyU: 'U',
    KeyI: 'I',
    KeyV: 'V',
    Quote: "'",
    Semicolon: ';',
    Comma: ',',
  };
  return keyMap[code] || code;
};

export type InfoShortcut = { type: 'info'; key: string };

export type BoltShortcut = { type: 'bolt'; slug: string };

export const handleBoltNavigation = (
  shortcut: BoltShortcut,
  e: KeyboardEvent,
  boltItems: BoltItem[],
  allCategories: Category[],
  dispatchCategoryActions: (categoryId: number) => void,
  navigateOrOpenAlert: (item: BoltItem) => void,
) => {
  const item = boltItems.find((i) => i.slug === shortcut.slug);
  const validCategory = allCategories.some(
    (cat: any) => cat.navigationCategoryId === item?.categoryId,
  );

  if (item?.categoryId == null || !validCategory) return;

  e.preventDefault();
  dispatchCategoryActions(item.categoryId);
  navigateOrOpenAlert(item);
};

export const dispatchCategoryActions = (
  categoryId: number,
  dispatch: Dispatch,
) => {
  if (categoryId !== CATEGORY_IDS.UNDERWRITER_FINAL_REVIEW) {
    dispatch(HomeActions.setIsUnderwriterCheckboxDisabled(false));
  }
  const categoryNavName = Object.keys(CATEGORY_IDS).find(
    (key) => CATEGORY_IDS[key] === categoryId,
  );

  // CRITICAL: Dispatch category changes FIRST, then clear alert
  dispatch(HomeActions.setClickedSelectedCategory(categoryNavName));
  dispatch(HomeActions.setSelectedCategory(categoryId));
  dispatch(HomeActions.setSelectedAlert(null));
  dispatch(HomeActions.setSelectedAlertParent(null));
};

export const navigateOrOpenAlert = (
  item: BoltItem,
  obfuscatedLenderDatabaseId: string,
  obfuscatedLoanRecordId: string,
  navigate: (to: string, options?: { replace?: boolean; state?: any }) => void,
  handleLeftDrawerCollapseForParentAlert: (
    alert: AllAlert,
    selectedAlert: AllAlert | null,
    dispatch: Dispatch,
  ) => void,
  dispatch: Dispatch,
) => {
  const boltPath = `/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/`;
  const currentPath = globalThis.location.pathname;
  const isOnBoltRoot = currentPath === boltPath;
  const isOnSummaryPage = currentPath.includes('/summary/');

  // Navigate to BOLT root if not there or on summary page
  if (!isOnBoltRoot || isOnSummaryPage) {
    navigate(boltPath);
  } else if (item.alert && handleLeftDrawerCollapseForParentAlert && dispatch) {
    // Only call this if we have the required dependencies (for legacy support)
    handleLeftDrawerCollapseForParentAlert(item.alert, null, dispatch);
  }
};

export const handleInfoNavigation = (
  shortcut: InfoShortcut,
  e: KeyboardEvent,
  infoItems: InfoItem[],
  navigate: (to: string, options?: { replace?: boolean; state?: any }) => void,
) => {
  const item = infoItems.find((i: InfoItem) => i.key === shortcut.key);
  if (item) {
    e.preventDefault();
    navigate(item.to);
  }
};
